import { TabType } from '../../../../renderApp/components/cells/Tabs/Tabs';
import { curry, filter, find, map, pipe, toArray } from '@fxts/core';

interface TabsGenerator<ID extends string> {
  tabs: TabType[];
  getSubTabsById(id: ID): TabType[];
}

export const MEMBERSHIP_PRODUCT_LIST_LIMIT_PER_PAGE = 60;

interface ExtendedTabType<ID extends string = string> extends TabType {
  id: ID;
  generateHref: (domain_name: string) => string;
  sub_tabs?: Omit<ExtendedTabType, 'sub_tabs'>[]; // 2 depth 까지만 지원
}

export type MembershipIdType = 'membership' | 'support' | 'commission';

const MEMBERSHIP_PAGE_TITLE_TABS: ExtendedTabType<MembershipIdType>[] = [
  {
    id: 'membership',
    generateHref(domain_name: string) {
      return `/${domain_name}/membership/main`;
    },
    label: ET('mps2::membership::main::title'),
    mo_no_padding: true,
    mo_no_underline: true,
    sub_tabs: [
      {
        id: 'membership',
        generateHref(domain_name: string) {
          return `/${domain_name}/membership/main`;
        },
        get label() {
          return ET('mps2::membership::join');
        },
      },
      {
        id: 'products',
        generateHref(domain_name: string) {
          return `/${domain_name}/membership/products`;
        },
        get label() {
          return ET('mps2::membership::product');
        },
      },
    ],
  },
  {
    id: 'support',
    generateHref(domain_name: string) {
      return `/${domain_name}/membership/support`;
    },
    label: ET('mps2::membership::support::title'),
    mo_no_padding: true,
    mo_no_underline: true,
  },
  {
    id: 'commission',
    generateHref(domain_name: string) {
      return `/${domain_name}/membership/commission`;
    },
    label: ET('mps2::membership::commission::title'),
    mo_no_padding: true,
    mo_no_underline: true,
  },
] as const;

const applyGenerateHref = curry(<T extends ExtendedTabType>(domain_name: string, data: T) => {
  return {
    ...data,
    href: data.generateHref(domain_name),
  };
});

/**
 * 크리에이터샵 > 멤버십 tab 데이터 만드는 함수
 */
export const generateCreatorMembershipTabs = (domain_name: string): TabsGenerator<MembershipIdType> => {
  const applyGenerateHrefWithDomainName = applyGenerateHref(domain_name);

  const tabs = pipe(
    MEMBERSHIP_PAGE_TITLE_TABS,
    map(applyGenerateHrefWithDomainName),
    map((tab) => ({
      ...tab,
      sub_tabs: pipe(tab.sub_tabs ?? [], map(applyGenerateHrefWithDomainName), toArray),
    })),
    toArray,
  );

  const getSubTabsById = (id: MembershipIdType): TabType[] => {
    const tab = find((tab) => tab.id === id, tabs);
    return tab ? tab.sub_tabs : [];
  };

  return {
    tabs,
    getSubTabsById,
  };
};

/**
 * 크리에이터샵 > 멤버십 tab을 하나만 만드는 함수
 */
export const generateCreatorMembershipTab = (
  domain_name: string,
  id: MembershipIdType,
): TabsGenerator<MembershipIdType> => {
  const applyGenerateHrefWithDomainName = applyGenerateHref(domain_name);

  const tabs = pipe(
    MEMBERSHIP_PAGE_TITLE_TABS,
    filter((MEMBERSHIP_PAGE_TITLE_TAB) => MEMBERSHIP_PAGE_TITLE_TAB.id === id),
    map(applyGenerateHrefWithDomainName),
    map((tab) => ({
      ...tab,
      sub_tabs: pipe(tab.sub_tabs ?? [], map(applyGenerateHrefWithDomainName), toArray),
    })),
    toArray,
  );

  const getSubTabsById = (id: MembershipIdType): TabType[] => {
    const tab = find((tab) => tab.id === id, tabs);
    return tab ? tab.sub_tabs : [];
  };

  return {
    tabs,
    getSubTabsById,
  };
};
